import React from 'react';
import { IconButton, Tooltip, makeStyles, Theme } from '@material-ui/core';
import { Brightness4 as DarkModeIcon, Brightness7 as LightModeIcon } from '@material-ui/icons';
import { useThemeMode } from '../context/ThemeContext';

const useStyles = makeStyles((theme: Theme) => ({
  toggleButton: {
    transition: 'all 0.3s ease',
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    marginLeft: 8,
    marginRight: 8,
    '&:hover': {
      transform: 'scale(1.1)',
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
    '&:focus': {
      backgroundColor: theme.palette.action.focus,
    },
  },
  iconLight: {
    color: '#FFA726',
  },
  iconDark: {
    color: '#424242',
  },
  icon: {
    transition: 'transform 0.3s ease',
    fontSize: '20px',
    color: theme.palette.text.primary,
  },
}));

interface ThemeToggleProps {
  className?: string;
  size?: 'small' | 'medium';
  showTooltip?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className,
  size = 'medium',
  showTooltip = true,
}) => {
  const classes = useStyles();
  const { mode, toggleTheme } = useThemeMode();

  const isDarkMode = mode === 'dark';
  const tooltipTitle = isDarkMode ? 'Switch to light mode' : 'Switch to dark mode';

  const button = (
    <IconButton
      onClick={toggleTheme}
      className={`${classes.toggleButton} ${className || ''}`}
      color="primary"
      size={size}
      aria-label={tooltipTitle}
    >
      {isDarkMode ? (
        <LightModeIcon className={`${classes.icon} ${classes.iconLight}`} />
      ) : (
        <DarkModeIcon className={`${classes.icon} ${classes.iconDark}`} />
      )}
    </IconButton>
  );

  if (showTooltip) {
    return (
      <Tooltip title={tooltipTitle} arrow>
        {button}
      </Tooltip>
    );
  }

  return button;
};
